/**
 * ConfidenceIndicator Component
 * Visual indicator showing AI confidence level for a suggestion.
 */
import React from 'react';
import type { ConfidenceLevel } from '../../types';

interface ConfidenceIndicatorProps {
  level: ConfidenceLevel;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const confidenceConfig = {
  high: {
    color: 'bg-green-500',
    textColor: 'text-green-700',
    bgColor: 'bg-green-100',
    label: 'High',
  },
  medium: {
    color: 'bg-yellow-500',
    textColor: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    label: 'Medium',
  },
  low: {
    color: 'bg-red-500',
    textColor: 'text-red-700',
    bgColor: 'bg-red-100',
    label: 'Low',
  },
};

const sizeConfig = {
  sm: { dot: 'w-2 h-2', text: 'text-xs' },
  md: { dot: 'w-3 h-3', text: 'text-sm' },
  lg: { dot: 'w-4 h-4', text: 'text-base' },
};

const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({
  level,
  showLabel = true,
  size = 'md',
}) => {
  const config = confidenceConfig[level];
  const sizes = sizeConfig[size];

  return (
    <div className="flex items-center space-x-1.5">
      <span
        data-testid="confidence-indicator"
        className={`${config.color} ${sizes.dot} rounded-full`}
      />
      {showLabel && (
        <span
          className={`${config.textColor} ${config.bgColor} ${sizes.text} px-2 py-0.5 rounded-full font-medium`}
        >
          {config.label}
        </span>
      )}
    </div>
  );
};

export default ConfidenceIndicator;
